import type { api } from "@healthos/schemas";
import type { QueryClient } from "@tanstack/react-query";
import { communityQueryKeys } from "./community";

// Centralised React Query invalidation rules for the Community frontend.
// Each mutation that changes feed content routes through these helpers so a
// new feed-affecting mutation cannot silently stale-render the feed.

/**
 * Invalidate the community feed and the You-tab "Your activity" counters.
 * Feed writes (moments, kudos, shares) change both surfaces; keep them in lockstep.
 */
export async function invalidateCommunityFeed(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: communityQueryKeys.feed }),
    queryClient.invalidateQueries({ queryKey: communityQueryKeys.meStats }),
  ]);
}

/** Invalidate the weekly recipe and schedule (used when a recipe is selected). */
export async function invalidateWeeklyRecipe(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: communityQueryKeys.weeklyRecipe() }),
    queryClient.invalidateQueries({ queryKey: communityQueryKeys.adminWeeklyRecipe }),
    queryClient.invalidateQueries({ queryKey: communityQueryKeys.adminWeeklyRecipeSchedule }),
  ]);
}

/** Invalidate admin community data (queues, items). */
export async function invalidateAdminCommunity(
  queryClient: QueryClient,
  activeQueue?: api.AdminCommunityQueue,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: communityQueryKeys.adminQueues }),
    queryClient.invalidateQueries({
      queryKey: communityQueryKeys.adminItems(
        (activeQueue ?? "company_review") as api.AdminCommunityQueue,
      ),
    }),
  ]);
}

/** Invalidate everything community-related (after a major change like a dev-seed). */
export async function invalidateAllCommunity(queryClient: QueryClient): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: communityQueryKeys.root });
}
